import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import api from '../../services/api'
import {
  GET_MY_TRANSPORT_REQUESTS,
  GET_TRANSPORT_REQUEST,
  POST_SUBMIT_PROPOSAL,
  POST_ACCEPT_COUNTER_PROPOSAL,
  PUT_REVISE_PROPOSAL,
  POST_NOMINATE_TRUCK,
  PUT_UPDATE_NOMINATION,
  PUT_CANCEL_NOMINATION,
  GET_TRUCK_NOMINATION
} from '../../helpers/url_helper'

// Async thunk for fetching my transport requests
export const fetchMyRequests = createAsyncThunk(
  'transportRequest/fetchMyRequests',
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const response = await api.get(`${GET_MY_TRANSPORT_REQUESTS}${queryParams ? `?${queryParams}` : ''}`)
      return {
        requests: response.data.data.requests,
        pagination: response.data.data.pagination
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch transport requests'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for fetching single transport request details
export const fetchRequestDetails = createAsyncThunk(
  'transportRequest/fetchRequestDetails',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_TRANSPORT_REQUEST}/${id}`)
      const data = response.data.data
      
      // Parse commodity_details JSON string
      let commodities = []
      if (data.commodity_details) {
        try {
          commodities = typeof data.commodity_details === 'string'
            ? JSON.parse(data.commodity_details)
            : data.commodity_details
        } catch (e) {
          console.error('Failed to parse commodity_details:', e)
        }
      }

      const creatorEntity = data.creator_entity || {
        role: data.creator_entity_role || null,
        entity_name: data.creator_entity_name || null,
      }
      
      // Transform the response to match component expectations
      return {
        ...data,
        commodities,
        id: data.id || data.request_id || null,
        request_id: data.request_id || data.id || null,
        proposals: data.proposals || [],
        transporters: data.transporters || [],
        creator_entity: creatorEntity,
        contractor_company_name: data.contractor_company?.company_name || '',
        contractor_company_id: data.contractor_company?.id || null,
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch request details'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for fetching my proposals for a request
export const fetchMyProposals = createAsyncThunk(
  'transportRequest/fetchMyProposals',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_TRANSPORT_REQUEST}/${requestId}`)
      // Proposals are included in request details as my_proposals
      return response.data.data.my_proposals || []
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch proposals'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for submitting proposal
export const submitProposal = createAsyncThunk(
  'transportRequest/submitProposal',
  async ({ requestId, proposalData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${POST_SUBMIT_PROPOSAL}/${requestId}/proposal`, proposalData)
      toast.success('Proposal submitted successfully')
      return response.data.proposal
    } catch (error) {
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Failed to submit proposal'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for accepting counter proposal
export const acceptCounterProposal = createAsyncThunk(
  'transportRequest/acceptCounterProposal',
  async ({ requestId, proposalId, acceptanceNotes }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${POST_ACCEPT_COUNTER_PROPOSAL}/${requestId}/accept-counter`, {
        proposal_id: proposalId,
        acceptance_notes: acceptanceNotes
      })
      toast.success('Counter proposal accepted successfully')
      return response.data.proposal
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to accept counter proposal'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for revising proposal
export const reviseProposal = createAsyncThunk(
  'transportRequest/reviseProposal',
  async ({ requestId, proposalId, revisionData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${PUT_REVISE_PROPOSAL}/${requestId}/proposal/${proposalId}/revise`, revisionData)
      toast.success('Proposal revised successfully')
      return response.data.proposal
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to revise proposal'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for nominating truck
export const nominateTruck = createAsyncThunk(
  'transportRequest/nominateTruck',
  async ({ requestId, proposalId, truckData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${POST_NOMINATE_TRUCK}/${requestId}/proposal/${proposalId}/nominate-truck`, truckData)
      toast.success('Truck nominated successfully')
      return response.data.nomination
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to nominate truck'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for updating nomination
export const updateNomination = createAsyncThunk(
  'transportRequest/updateNomination',
  async ({ requestId, proposalId, nominationId, nominationData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${PUT_UPDATE_NOMINATION}/${requestId}/proposal/${proposalId}/nomination/${nominationId}`,
        nominationData
      )
      toast.success('Nomination updated successfully')
      return response.data.data || response.data.nomination || response.data
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update nomination'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for canceling nomination
export const cancelNomination = createAsyncThunk(
  'transportRequest/cancelNomination',
  async ({ requestId, proposalId, nominationId }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `${PUT_CANCEL_NOMINATION}/${requestId}/proposal/${proposalId}/nomination/${nominationId}/cancel`
      )
      toast.success('Nomination canceled successfully')
      return response.data.data || response.data.nomination || response.data
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to cancel nomination'
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

// Async thunk for fetching truck nomination
export const fetchTruckNomination = createAsyncThunk(
  'transportRequest/fetchTruckNomination',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${GET_TRUCK_NOMINATION}/${requestId}`)
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch nomination'
      // Silently ignore 401 (handled by interceptor), 404 (no nomination) and access denied errors
      const isAccessDenied = error.response?.status === 403 ||
        message?.toLowerCase().includes('access denied') ||
        message?.toLowerCase().includes('does not belong')
      if (error.response?.status !== 401 && error.response?.status !== 404 && !isAccessDenied) {
        toast.error(message)
      }
      return rejectWithValue(null)
    }
  }
)

// Async thunk for fetching all proposals across all requests
export const fetchAllProposals = createAsyncThunk(
  'transportRequest/fetchAllProposals',
  async (_, { rejectWithValue }) => {
    try {
      // First, fetch all requests to get the list with proposal status
      const requestsResponse = await api.get(`${GET_MY_TRANSPORT_REQUESTS}`)
      const requests = requestsResponse.data.data.requests || []
      
      
      // Filter requests that have proposals (my_proposal_status indicates proposal exists)
      const requestsWithProposals = requests.filter(req => 
        req.my_proposal_status && 
        req.my_proposal_status !== 'invited' && 
        req.my_proposal_status !== 'viewed'
      )
      
      
      // Fetch details for each request to get the my_proposals array
      const allProposals = []
      
      for (const request of requestsWithProposals) {
        try {
          const detailResponse = await api.get(`${GET_TRANSPORT_REQUEST}/${request.request_id}`)
          const requestData = detailResponse.data.data
          
          if (requestData.my_proposals && Array.isArray(requestData.my_proposals)) {
            requestData.my_proposals.forEach(proposal => {
              allProposals.push({
                ...proposal,
                request_id: requestData.request_id,
                request_number: requestData.request_number,
                origin: requestData.origin,
                destination: requestData.destination,
              })
            })
          }
        } catch (err) {
          console.error(`Failed to fetch details for request ${request.request_id}:`, err)
        }
      }
      
      return allProposals
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch proposals'
      if (error.response?.status !== 401) toast.error(message)
      return rejectWithValue(message)
    }
  }
)

const initialState = {
  requests: [],
  selectedRequest: null,
  proposals: [],
  allProposals: [],
  selectedProposal: null,
  nomination: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRequests: 0,
  },
}

const transportRequestSlice = createSlice({
  name: 'transportRequest',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    clearSelectedRequest: state => {
      state.selectedRequest = null
    },
    clearProposals: state => {
      state.proposals = []
    },
    clearNomination: state => {
      state.nomination = null
    },
  },
  extraReducers: builder => {
    builder
      // Fetch My Requests
      .addCase(fetchMyRequests.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMyRequests.fulfilled, (state, action) => {
        state.loading = false
        state.requests = action.payload.requests
        state.pagination = action.payload.pagination
      })
      .addCase(fetchMyRequests.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Request Details
      .addCase(fetchRequestDetails.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRequestDetails.fulfilled, (state, action) => {
        state.loading = false
        state.selectedRequest = action.payload
      })
      .addCase(fetchRequestDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch My Proposals
      .addCase(fetchMyProposals.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMyProposals.fulfilled, (state, action) => {
        state.loading = false
        state.proposals = action.payload
      })
      .addCase(fetchMyProposals.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Submit Proposal
      .addCase(submitProposal.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(submitProposal.fulfilled, (state, action) => {
        state.loading = false
        state.proposals.push(action.payload)
      })
      .addCase(submitProposal.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Accept Counter Proposal
      .addCase(acceptCounterProposal.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(acceptCounterProposal.fulfilled, (state, action) => {
        state.loading = false
        const index = state.proposals.findIndex(p => p.id === action.payload.id)
        if (index !== -1) {
          state.proposals[index] = action.payload
        }
      })
      .addCase(acceptCounterProposal.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Revise Proposal
      .addCase(reviseProposal.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(reviseProposal.fulfilled, (state, action) => {
        state.loading = false
        state.proposals.push(action.payload)
      })
      .addCase(reviseProposal.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Nominate Truck
      .addCase(nominateTruck.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(nominateTruck.fulfilled, (state, action) => {
        state.loading = false
        state.nomination = action.payload
      })
      .addCase(nominateTruck.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Truck Nomination
      .addCase(fetchTruckNomination.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTruckNomination.fulfilled, (state, action) => {
        state.loading = false
        state.nomination = action.payload
      })
      .addCase(fetchTruckNomination.rejected, (state, action) => {
        state.loading = false
        state.nomination = null
      })
      
      // Fetch All Proposals
      .addCase(fetchAllProposals.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllProposals.fulfilled, (state, action) => {
        state.loading = false
        state.allProposals = action.payload
      })
      .addCase(fetchAllProposals.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, clearSelectedRequest, clearProposals, clearNomination } = transportRequestSlice.actions
export default transportRequestSlice.reducer
